/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import { Either, Left, Right } from 'purify-ts/Either';
import { EitherAsync } from 'purify-ts/EitherAsync';
import { Maybe } from 'purify-ts/Maybe';
import { getStoreBuilder } from 'vuex-typex';
import * as api from '@codegrade/api';
import { assert, object, typeHelpers } from '@codegrade/utils';
import client from '@/client';
import * as models from '@/models';
import { AssignmentsStore, FeedbackStore, FileTreeStore, GradersStore } from '@/store';
import * as utils from '@/utils';
import { PeerFeedbackStore } from './peer_feedback';
const storeBuilder = getStoreBuilder();
const SUBMISSION_STATE_KEYS = typeHelpers.keysOfFast([
    'submissions',
    'latestSubmissions',
    'submissionsByUser',
    'submissionsByUserLoaders',
    'submissionsLoaders',
    'singleSubmissionLoaders',
    'groupSubmissionUsers',
]);
const moduleBuilder = storeBuilder.module('submissions', {
    submissions: {},
    latestSubmissions: {},
    submissionsByUser: {},
    submissionsByUserLoaders: {},
    submissionsLoaders: {},
    singleSubmissionLoaders: {},
    groupSubmissionUsers: {},
});
/**
 * Maybe add a submission to the latest submission.
 *
 * @param submissionLookup The lookup to use to retrieve the submissions.
 * @param latestSubs The current latest known submissions.
 * @param newSub The submission that might be the new latest submission for a user.
 * @returns `ltestSubs`, which is also mutated in place.
 */
function addToLatest(submissionLookup, latestSubs, newSub) {
    const { userId } = newSub;
    Maybe.fromNullable(latestSubs[userId])
        .chain(object.getItem(submissionLookup))
        .filter(foundLatest => {
        if (foundLatest.createdAt.isAfter(newSub.createdAt)) {
            return true;
        }
        else if (foundLatest.createdAt.isBefore(newSub.createdAt)) {
            return false;
        } /* The times are the same. */
        else {
            // Make sure we still have a consistent ordering.
            return foundLatest.id > newSub.id;
        }
    })
        .ifNothing(() => {
        utils.vueSet(latestSubs, userId, newSub.id);
    });
    return latestSubs;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export var SubmissionsStore;
(function (SubmissionsStore) {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.getSingleSubmission = moduleBuilder.read(state => object.getItem(state.submissions), 'getSingleSubmission');
    // eslint-disable-next-line no-underscore-dangle
    const _getLatestSubmissionIds = moduleBuilder.read(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    state => (assignmentId) => Maybe.fromNullable(state.latestSubmissions[assignmentId]).map(x => Object.values(x)), '_getLatestSubmissionIds');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.getLatestSubmissions = moduleBuilder.read(() => {
        const getLatestIds = _getLatestSubmissionIds();
        const getSingle = SubmissionsStore.getSingleSubmission();
        return (assignmentId) => getLatestIds(assignmentId).map(latest => Maybe.catMaybes(latest.map(getSingle)));
    }, 'getLatestSubmissions');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.getGroupSubmissionOfUser = moduleBuilder.read(state => {
        const lookup = state.groupSubmissionUsers;
        const getSingle = SubmissionsStore.getSingleSubmission();
        return (assignmentId, userId) => Maybe.fromNullable(lookup[assignmentId])
            .chainNullable(subs => subs[userId])
            .map(getSingle);
    }, 'getGroupSubmissionOfUser');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.getLatestSubmissionByUser = moduleBuilder.read(state => {
        const getSingle = SubmissionsStore.getSingleSubmission();
        const getGroupSub = SubmissionsStore.getGroupSubmissionOfUser();
        return (assignmentId, userId) => getGroupSub(assignmentId, userId)
            .join()
            .altLazy(() => Maybe.fromNullable(state.latestSubmissions[assignmentId]?.[userId]).chain(getSingle));
    }, 'getLatestSubmissionByUser');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.getSubmissionsByUser = moduleBuilder.read(state => {
        const lookup = state.submissionsByUser;
        const subGetter = SubmissionsStore.getSingleSubmission();
        const groupSubGetter = SubmissionsStore.getGroupSubmissionOfUser();
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        const subsByUserGetter = (assignmentId, userId, { includeGroupSubmissions = false } = {}) => {
            let res = Maybe.fromNullable(lookup[assignmentId])
                .chainNullable(subs => subs[userId])
                .map(subIds => Maybe.catMaybes(Array.from(subIds).map(subGetter)));
            if (includeGroupSubmissions) {
                groupSubGetter(assignmentId, userId)
                    .join()
                    .chain(latestGroupSub => subsByUserGetter(assignmentId, latestGroupSub.userId))
                    .ifJust(groupSubs => {
                    res = Maybe.of(res.orDefault([]).concat(groupSubs));
                });
            }
            return res;
        };
        return subsByUserGetter;
    }, 'getSubmissionsByUser');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.getIsLatestSubmissionByUser = moduleBuilder.read(() => {
        const getLatestByUser = SubmissionsStore.getLatestSubmissionByUser();
        return (submission) => {
            const { assignmentId, user } = submission;
            return getLatestByUser(assignmentId, user.id).caseOf({
                Just: sub => sub.id === submission.id,
                Nothing: () => true,
            });
        };
    }, 'getIsLatestSubmissionByUser');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    const getUsersInGroup = (ids) => Object.values(ids).reduce((acc, id) => {
        SubmissionsStore.getSingleSubmission()(id).ifJust(sub => {
            if (sub.user.isGroup) {
                for (const member of sub.user.group.members) {
                    acc[member.id] = sub.id;
                }
            }
        });
        return acc;
    }, {});
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.commitUpdateSubmissions = moduleBuilder.commit((state, 
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    payload) => {
        const { assignmentId, submissions } = payload;
        const newLatest = {};
        const byUser = {};
        for (const sub of submissions) {
            // BLAZE IT: R y a n C e l s i u s ° S o u n d s
            utils.vueSet(state.submissions, sub.id, sub);
            if (!object.hasAttr(byUser, sub.userId)) {
                byUser[sub.userId] = new Set();
            }
            addToLatest(state.submissions, newLatest, sub);
            byUser[sub.userId].add(sub.id);
        }
        utils.vueSet(state.latestSubmissions, assignmentId, newLatest);
        utils.vueSet(state.groupSubmissionUsers, assignmentId, getUsersInGroup(newLatest));
        utils.vueSet(state.submissionsByUser, assignmentId, byUser);
        utils.vueSet(state.submissionsByUserLoaders, assignmentId, {});
    }, 'commitUpdateSubmissions');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.commitSubmissionsByUserLoader = moduleBuilder.commit((state, payload) => {
        const { assignmentId, userId, loader } = payload;
        const assignmentLoaders = state.submissionsByUserLoaders[assignmentId] ?? {};
        if (loader == null) {
            utils.vueDelete(assignmentLoaders, userId);
        }
        else {
            utils.vueSet(assignmentLoaders, userId, loader);
        }
        utils.vueSet(state.submissionsByUserLoaders, assignmentId, assignmentLoaders);
    }, 'commitSubmissionsByUserLoader');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.commitUpdateSubmission = moduleBuilder.commit((state, 
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    payload) => {
        const { submissionId, submissionProps } = payload;
        SubmissionsStore.getSingleSubmission()(submissionId).caseOf({
            Just: submission => {
                utils.vueSet(state.submissions, submissionId, submission.update(submissionProps));
            },
            Nothing: () => {
                throw new Error(`Submission not found: ${submissionId}`);
            },
        });
    }, 'commitUpdateSubmission');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.commitSubmissionsLoader = moduleBuilder.commit((state, payload) => {
        const { assignmentId, loader } = payload;
        utils.vueSet(state.submissionsLoaders, assignmentId, loader);
    }, 'commitSubmissionsLoader');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.commitSingleSubmissionLoader = moduleBuilder.commit((state, payload) => {
        const { submissionId, loader } = payload;
        if (loader == null) {
            utils.vueDelete(state.singleSubmissionLoaders, submissionId);
        }
        else {
            utils.vueSet(state.singleSubmissionLoaders, submissionId, loader);
        }
    }, 'commitSingleSubmissionLoader');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.commitSingleSubmission = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => {
        const { submission } = payload;
        const { id, assignmentId, userId } = submission;
        utils.vueSet(state.submissions, id, submission);
        const assignmentUserSubmissions = state.submissionsByUser[assignmentId] ?? {};
        const userSubmissions = assignmentUserSubmissions[userId] ?? new Set();
        assignmentUserSubmissions[userId] = userSubmissions;
        userSubmissions.add(id);
        utils.vueSet(state.submissionsByUser, assignmentId, assignmentUserSubmissions);
        const oldLatest = state.latestSubmissions[assignmentId] ?? {};
        const newLatest = addToLatest(state.submissions, oldLatest, submission);
        utils.vueSet(state.latestSubmissions, assignmentId, newLatest);
        utils.vueSet(state.groupSubmissionUsers, assignmentId, getUsersInGroup(newLatest));
    }, 'commitSingleSubmission');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.commitClearAll = moduleBuilder.commit(state => {
        for (const key of SUBMISSION_STATE_KEYS) {
            utils.vueSet(state, key, {});
        }
    }, 'commitClearAll');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    const clearAssignmentInfo = (context, assignmentId) => {
        FileTreeStore.commitDeleteFileTree({ assignmentId });
        FeedbackStore.commitDeleteFeedback({ assignmentId });
        return EitherAsync(() => 
        // @ts-expect-error: vuex-typex does not declare the dispatch method on context
        context.dispatch('rubrics/clearResult', { submissionIds: _getLatestSubmissionIds()(assignmentId).orDefault([]) }, { root: true }));
    };
    // This needs to be in one promise to make sure that two very quick
    // calls to `loadSubmissions` still only does one request (for the
    // same arguments of course).
    /* eslint-disable-next-line no-underscore-dangle, jsdoc/require-jsdoc -- When touching please add documentation. */
    SubmissionsStore._loadSubmissions = (courseId, assignmentId, force) => utils.eitherAsyncUtils
        .all([
        client.assignment
            .getAllSubmissions({
            assignmentId,
            extended: true,
            latestOnly: true,
        })
            .chain(response => EitherAsync.liftEither(response.mapEither(cgResult => Either.sequence(cgResult.map(sub => {
            if (sub instanceof api.ExtendedWork) {
                return Right(models.Submission.fromApiServerData(sub));
            }
            assert.AssertionError.typeAssert(sub);
            return Left(new Error('Expected an instance of ExtendedWork'));
        })).ifRight(submissions => {
            SubmissionsStore.commitUpdateSubmissions({
                assignmentId,
                submissions,
            });
        })))),
        EitherAsync(() => AssignmentsStore.loadSingleAssignment({ courseId, assignmentId })).mapLeft(utils.coerceToError),
        EitherAsync(() => GradersStore.loadGraders({
            assignmentId,
            force,
        }).catch(utils.makeHttpErrorHandler({
            403: () => null,
        }))).mapLeft(utils.coerceToError),
    ])
        .map(([subs]) => subs);
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.loadSubmissions = moduleBuilder.dispatch((context, 
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    payload) => {
        const { courseId, assignmentId, force } = payload;
        return EitherAsync.fromPromise(() => {
            if (force === true || context.state.submissionsLoaders[assignmentId] == null) {
                const loader = clearAssignmentInfo(context, assignmentId)
                    .ifRight(() => {
                    // It is possible that in the mean time these promises were
                    // cleared. Simply store them again to prevent double loading.
                    SubmissionsStore.commitSubmissionsLoader({
                        assignmentId,
                        loader,
                    });
                })
                    .chain(() => SubmissionsStore._loadSubmissions(courseId, assignmentId, force))
                    .run();
                SubmissionsStore.commitSubmissionsLoader({
                    assignmentId,
                    loader,
                });
            }
            return context.state.submissionsLoaders[assignmentId];
        });
    }, 'loadSubmissions');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.loadSingleSubmission = moduleBuilder.dispatch((context, payload) => {
        const { courseId, assignmentId, submissionId, force } = payload;
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        const getSub = () => {
            if (force === true) {
                return EitherAsync.liftEither(Left(undefined));
            }
            const sub = SubmissionsStore.getSingleSubmission()(submissionId).toEither(undefined);
            return EitherAsync.liftEither(sub).chain(utils.responseWithValue);
        };
        return getSub()
            .chainLeft(() => SubmissionsStore.loadSubmissions({ courseId, assignmentId }).chain(() => {
            if (force === true) {
                return EitherAsync.liftEither(Left(undefined));
            }
            if (context.state.singleSubmissionLoaders[submissionId] != null) {
                return EitherAsync.fromPromise(() => context.state.singleSubmissionLoaders[submissionId]);
            }
            return getSub();
        }))
            .chainLeft(() => {
            const loader = client.submission
                .get({ submissionId })
                .chain(response => EitherAsync.liftEither(response.mapEither(cgResult => {
                if (!(cgResult instanceof api.ExtendedWork)) {
                    assert.AssertionError.typeAssert(cgResult);
                    return Left(new Error('Expected an instance of ExtendedWork'));
                }
                const submission = models.Submission.fromApiServerData(cgResult);
                SubmissionsStore.commitSingleSubmission({ submission });
                SubmissionsStore.commitSingleSubmissionLoader({
                    submissionId,
                    loader: null,
                });
                return Right(submission);
            })))
                .run();
            SubmissionsStore.commitSingleSubmissionLoader({ submissionId, loader });
            return loader;
        });
    }, 'loadSingleSubmission');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.loadGivenSubmissions = moduleBuilder.dispatch((_, payload) => {
        const { courseId, assignmentId, submissionIds } = payload;
        return utils.eitherAsyncUtils.all(Array.from(submissionIds, submissionId => SubmissionsStore.loadSingleSubmission({ courseId, assignmentId, submissionId })));
    }, 'loadGivenSubmissions');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.loadSubmissionsByUser = moduleBuilder.dispatch((context, payload) => {
        const { courseId, assignmentId, userId, force } = payload;
        return SubmissionsStore.loadSubmissions({ courseId, assignmentId }).chain(() => {
            if (force === true ||
                context.state.submissionsByUserLoaders[assignmentId]?.[userId] == null) {
                const loader = client.assignment
                    .getSubmissionsByUser({ assignmentId, userId })
                    .map(response => response.map(cgResult => cgResult.map(sub => {
                    const submission = models.Submission.fromApiServerData(sub);
                    SubmissionsStore.commitSingleSubmission({ submission });
                    return submission;
                })))
                    .run();
                SubmissionsStore.commitSubmissionsByUserLoader({ assignmentId, userId, loader });
            }
            return context.state.submissionsByUserLoaders[assignmentId][userId];
        });
    }, 'loadSubmissionsByUser');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.loadLatestSubmissionsByUserInCourse = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (_, payload) => {
        const { courseId, userId } = payload;
        return client.course
            .getSubmissionsByUser({
            courseId,
            userId,
            latestOnly: true,
        })
            .map(response => response.map(cgResult => object.mapObject(cgResult, ([sub]) => Maybe.fromNullable(sub)
            .map(models.Submission.fromApiServerData)
            .ifJust(submission => SubmissionsStore.commitSingleSubmission({ submission })))));
    }, 'loadLatestSubmissionsByUserInCourse');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.createSubmission = moduleBuilder.dispatch((_, payload) => {
        const { assignmentId, authorId, isTestSubmission, ignoredFiles, requestBody } = payload;
        return client.assignment
            .uploadSubmission({
            assignmentId,
            authorId,
            isTestSubmission,
            ignoredFiles,
            requestBody,
        })
            .map(response => response.map(cgResult => {
            const submission = models.Submission.fromApiServerData(cgResult);
            PeerFeedbackStore.commitClearConnections({ assignmentId });
            SubmissionsStore.commitSingleSubmission({ submission });
            return submission;
        }));
    }, 'createSubmission');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.patchSubmission = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (_, payload) => client.submission.patch(payload).map(response => response.map(cgResult => {
        const submission = models.Submission.fromApiServerData(cgResult);
        SubmissionsStore.commitSingleSubmission({ submission });
        return submission;
    })), 'patchSubmission');
    const commitDeleteSubmission = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => {
        utils.vueDelete(state.submissions, payload.submissionId);
    }, 'commitDeleteSubmission');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.deleteSubmission = moduleBuilder.dispatch((_, 
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    payload) => {
        const { courseId, assignmentId, submissionId } = payload;
        return client.submission.delete({ submissionId }).map(resp => resp.addCallbacks({
            onAfterSuccess: () => SubmissionsStore.loadSubmissions({ courseId, assignmentId, force: true })
                .run()
                .then(x => x.unsafeCoerce())
                .then(() => commitDeleteSubmission(payload)),
        }));
    }, 'deleteSubmission');
    utils.signal.AFTER_USER_LOGGED_OUT.register(() => {
        SubmissionsStore.commitClearAll();
    });
})(SubmissionsStore || (SubmissionsStore = {}));
